import React, { useState, useEffect } from 'react';
import { keyframes } from '@mui/system';
import CounterAnimation from '../components/CounterAnimation';
import teamImage from '../assets/images/team.jpg';
import serverImage from '../assets/images/hpe_servers/hpe-proliant-dl360-gen12.png';
import storageImage from '../assets/images/hpe_servers/HPE_Alletra_Storage_MP_B10000.png';
import microserverImage from '../assets/images/hpe_servers/HPE_MicroServer_Gen10_Hero.png';
import superdomeImage from '../assets/images/hpe_servers/HPE_Superdome_Flex_Server.png';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingUp as TrendingIcon,
  Shield as ShieldIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  Business as BusinessIcon,
  CloudDone as UptimeIcon,
  Favorite as RetentionIcon,
} from '@mui/icons-material';

const countUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('about');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);
  const whyChooseUs = [
    {
      icon: <ShieldIcon />,
      title: 'Proven Expertise',
      description: 'Certified enterprise infrastructure professionals with extensive deployment experience'
    },
    {
      icon: <TrendingIcon />,
      title: 'Modern Technology',
      description: 'Industry-leading virtualization, cloud, and data protection solutions'
    },
    {
      icon: <SpeedIcon />,
      title: 'Rapid Response',
      description: '24/7 expert support with industry-leading response times for critical issues'
    },
    {
      icon: <CheckIcon />,
      title: 'Flexible Consumption Models',
      description: 'Pay-per-use and as-a-Service options for CapEx-free infrastructure'
    },
    {
      icon: <SupportIcon />,
      title: 'Business Continuity',
      description: 'Comprehensive disaster recovery and data protection strategies'
    },
  ];

  const stats = [
    { 
      value: '50+', 
      label: 'Businesses Served',
      icon: <BusinessIcon />,
      color: 'success.main'
    },
    { 
      value: '99.999%*', 
      label: 'Uptime Maintained',
      icon: <UptimeIcon />,
      color: 'primary.main'
    },
    { 
      value: '95%', 
      label: 'Client Retention',
      icon: <RetentionIcon />,
      color: 'error.main'
    },
  ];



  return (
    <Box id="about" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        {/* Company Overview */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" component="h2" gutterBottom>
            IT Infrastructure Experts You Can Trust
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            TNCOSOFT specializes in enterprise-grade IT infrastructure solutions for businesses of all sizes. 
            As a certified partner, we combine world-class technology with expert service to help organizations 
            modernize their infrastructure.
          </Typography>
        </Box>

        {/* Stats */}
        <Grid container spacing={4} sx={{ mb: 8, justifyContent: 'center' }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  height: '100%',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: isVisible ? `${countUp} 0.6s ease-out ${index * 0.1}s both` : 'none',
                  '&:hover': {
                    transform: 'translateY(-12px) scale(1.05)',
                  },
                }}
              >
                <CardContent>
                  <Avatar 
                    sx={{ 
                      bgcolor: stat.color,
                      width: 64,
                      height: 64,
                      mx: 'auto',
                      mb: 3,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'rotate(360deg) scale(1.1)',
                      },
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <CounterAnimation
                    end={stat.value === '50+' ? 50 : stat.value === '99.999%*' ? 99.999 : 95}
                    suffix={stat.value === '50+' ? '+' : stat.value === '99.999%*' ? '%*' : '%'}
                    variant="h3"
                    duration={2500}
                    sx={{ 
                      color: stat.color, 
                      fontWeight: 800,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      mb: 1,
                    }}
                  />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Infrastructure Showcase */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h3" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
            Enterprise-Grade Infrastructure
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4, maxWidth: 700, mx: 'auto' }}>
            We deploy and support cutting-edge infrastructure solutions from industry-leading vendors
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                sx={{
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={serverImage}
                  alt="Enterprise ProLiant Servers - High-performance rack servers for virtualization"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'contain',
                    p: 2,
                    bgcolor: '#ffffff',
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                sx={{
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(240, 147, 251, 0.4)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={storageImage}
                  alt="Alletra Storage Solutions - Enterprise storage with AI-driven automation"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'contain',
                    p: 2,
                    bgcolor: '#ffffff',
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                sx={{
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={microserverImage}
                  alt="MicroServer Gen10 - Compact servers for edge computing and SMB"                  loading="lazy"                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'contain',
                    p: 2,
                    bgcolor: '#ffffff',
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                sx={{
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(250, 112, 154, 0.4)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={superdomeImage}
                  alt="Superdome Flex - Mission-critical infrastructure for high-performance computing"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'contain',
                    p: 2,
                    bgcolor: '#ffffff',
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h3" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
            Why Leading Companies Trust Us
          </Typography>
          <List>
            {whyChooseUs.map((item, index) => (
              <ListItem 
                key={index} 
                sx={{ 
                  mb: 2,
                  animation: isVisible ? `${slideInLeft} 0.6s ease-out ${index * 0.1 + 0.5}s both` : 'none',
                }}
              >
                <ListItemIcon>
                  <Avatar sx={{ 
                    bgcolor: 'primary.main',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1) rotate(5deg)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    },
                  }}>
                    {item.icon}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="h6" component="h4">
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1" color="text.secondary">
                      {item.description}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>



        {/* Team Image */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <img
            src={teamImage}
            alt="IT infrastructure professionals collaborating in a modern office environment"
            loading="lazy"
            style={{
              width: '100%',
              maxWidth: '800px',
              height: '300px',
              objectFit: 'cover',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }}
          />
        </Box>

        {/* Mission Statement */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Paper sx={{ 
            p: 4, 
            backgroundColor: 'primary.main', 
            color: 'white',
          }}>
            <Typography variant="h4" component="h3" gutterBottom>
              Our Mission
            </Typography>
            <Typography variant="h6">
              To empower organizations with robust, scalable, and secure IT infrastructure solutions that enable them 
              to focus on innovation and growth while we ensure their technology foundation is rock-solid.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default About;